/* Org Teams ▸ Venues — the org's HOME venues, per sport (AJ 2026-10-13).
 * Same venues data as WP Mgmt ▸ Venues; the "Sport" checkboxes set which sports
 * call this venue home (`venues.home_sports`, OL-only). A sport can have more
 * than one home venue (e.g. two baseball fields) — the first is the default
 * Location for a HOME game in the Scheduler. Unticking every sport removes the
 * venue from this list (it stays in the data, under Opponents ▸ Venues).
 * Doc: /app/docs/AD_TEAMS_CONFIGURATOR.md §Home venues. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import bridgeApi from '../admin/bridgeApi';
import AddVenueModal from '../teams/AddVenueModal';
import { thCls } from './adStaffShared';

const API = process.env.REACT_APP_BACKEND_URL;
const decode = (s) => { if (!s || !s.includes('&')) return s || ''; const t = document.createElement('textarea'); t.innerHTML = s; return t.value; };

export default function OrgHomeVenues({ orgId, isDark }) {
  const [rows, setRows] = useState(null);
  const [sports, setSports] = useState([]);
  const [pick, setPick] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, s] = await Promise.all([
        axios.get(`${API}/api/ad-teams/${orgId}/home-venues`),
        axios.get(`${API}/api/organizations/${orgId}/sports`),
      ]);
      setRows(v.data.data || []);
      setSports((s.data || []).filter((x) => x.slug).map((x) => ({ code: x.slug, name: x.name || x.label || x.slug })));
    } catch (e) { setRows([]); window.alert(e?.response?.data?.detail || 'Could not load venues'); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const err = (e, fb) => window.alert(e?.response?.data?.detail || fb);
  const setSportsFor = async (v, list) => {
    setBusy(v.id);
    try { await axios.put(`${API}/api/ad-teams/${orgId}/home-venues/${v.id}`, { sports: list }); await load(); }
    catch (e) { err(e, 'Save failed'); } finally { setBusy(null); }
  };
  const patch = async (v, field, val) => {
    if ((val || '') === (v[field] || '')) return;
    try { await bridgeApi.patch('/venues', orgId, v.id, { [field]: val }); await load(); } catch (e) { err(e, 'Save failed'); }
  };

  if (rows === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  const home = rows.filter((r) => r.is_home);
  const others = rows.filter((r) => !r.is_home);
  const cell = `w-full bg-transparent rounded px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  const txt = (v, k, ph) => (
    <input defaultValue={decode(v[k])} key={`${v.id}-${k}-${v[k]}`} placeholder={ph} className={cell}
      onBlur={(e) => patch(v, k, e.target.value.trim())} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      data-testid={`homevenue-${k}-${v.id}`} />
  );
  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
      <CardContent className="p-4 space-y-3" data-testid="org-home-venues">
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Our home venues and the sports that play there. The first venue for a sport (A→Z, as listed) is the default Location for a
          home game in the Schedule (it can still be changed per game).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse"><thead><tr>
            <th className={thCls(isDark)}>Venue</th><th className={thCls(isDark)}>Street</th><th className={thCls(isDark)}>City</th>
            <th className={thCls(isDark)}>State</th><th className={thCls(isDark)}>Zip</th>
            <th className={thCls(isDark)} title="Sports that call this venue home">Sport</th>
            <th className={thCls(isDark)}>Map link</th><th className={thCls(isDark) + ' w-6'} />
          </tr></thead><tbody>
            {home.map((v) => (
              <tr key={v.id} className={isDark ? 'border-t border-slate-700/50' : 'border-t border-slate-100'} data-testid={`homevenue-row-${v.id}`}>
                <td className="px-1 py-1 min-w-[180px]">{txt(v, 'title', 'Venue name')}</td>
                <td className="px-1 py-1 min-w-[150px]">{txt(v, 'address_1', 'Street')}</td>
                <td className="px-1 py-1 min-w-[100px]">{txt(v, 'city', 'City')}</td>
                <td className="px-1 py-1 w-14">{txt(v, 'state', 'ST')}</td>
                <td className="px-1 py-1 w-20">{txt(v, 'zip', 'Zip')}</td>
                <td className="px-1 py-1 whitespace-nowrap">
                  {sports.map((s) => {
                    const on = v.home_sports.includes(s.code);
                    return (
                      <label key={s.code} className={`mr-2 inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                        <input type="checkbox" checked={on} disabled={busy === v.id} data-testid={`homevenue-sport-${v.id}-${s.code}`}
                          onChange={() => setSportsFor(v, on ? v.home_sports.filter((x) => x !== s.code) : [...v.home_sports, s.code])} />
                        {s.name}
                      </label>
                    );
                  })}
                </td>
                <td className="px-1 py-1 min-w-[130px]">{txt(v, 'map_url', 'https://maps…')}</td>
                <td className="px-1 py-1">
                  <button type="button" title="Not a home venue (keeps the venue, removes it from this list)"
                    onClick={() => setSportsFor(v, [])} className="p-1 text-slate-400 hover:text-red-500"
                    data-testid={`homevenue-remove-${v.id}`}><X className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            <tr className={isDark ? 'border-t border-slate-700/50' : 'border-t border-slate-100'}>
              <td className="px-1 py-2" colSpan={8}>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="homevenue-pick"
                    className={`h-8 text-xs rounded px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                    <option value="">Add an existing venue as a home venue…</option>
                    {others.map((v) => <option key={v.id} value={v.id}>{decode(v.title)}</option>)}
                  </select>
                  <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700 text-white" disabled={!pick || !sports.length}
                    data-testid="homevenue-add-existing"
                    onClick={async () => { const v = others.find((x) => x.id === pick); if (v) { await setSportsFor(v, [sports[0].code]); setPick(''); } }}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
                  <span className="text-xs text-slate-400">or</span>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setAdding(true)} data-testid="homevenue-add-new">
                    <Plus className="h-3.5 w-3.5 mr-1" />New venue</Button>
                  <span className="text-[11px] text-slate-400">then tick its sports.</span>
                </div>
              </td>
            </tr>
          </tbody></table>
        </div>
        <AddVenueModal open={adding} orgId={orgId} onClose={() => setAdding(false)}
          onCreated={async (v) => { if (v?.ol_id && sports.length) await setSportsFor({ id: v.ol_id, home_sports: [] }, [sports[0].code]); else load(); }} />
      </CardContent>
    </Card>
  );
}
